import React, { useEffect, useState } from 'react';
import inspectionService from '../../services/inspectionService';
import { formatCategory } from '../../pages/inspections/categoryOptions';
import './InspectionDetailModal.css';

const ITEM_STATUS_LABEL = {
  compliant: 'Compliant',
  non_compliant: 'Non-Compliant',
  not_applicable: 'N/A',
};

const OVERALL_LABEL = {
  compliant: 'Fully Compliant',
  partial: 'Partially Compliant',
  non_compliant: 'Non-Compliant',
};

const FINDING_STATUS_LABEL = { not_done: 'Not Done', partially_done: 'Partially Done', done: 'Done' };

const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');

/**
 * Full read-only detail view for a single submitted Inspection: the
 * checklist snapshot at submission time, overall findings/recommendations,
 * any ComplianceFindings auto-raised from it, GPS location, and attached
 * evidence photos.
 *
 * Props:
 *  - inspectionId: string | null - when set, fetches and shows that inspection
 *  - onClose: () => void
 */
const InspectionDetailModal = ({ inspectionId, onClose }) => {
  const [inspection, setInspection] = useState(null);
  const [findings, setFindings] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!inspectionId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [inspectionData, evidenceData] = await Promise.all([
          inspectionService.getById(inspectionId),
          inspectionService.getEvidence(inspectionId).catch(() => ({ evidence: [] })),
        ]);
        if (cancelled) return;
        const loadedInspection = inspectionData.inspection;
        setInspection(loadedInspection);
        setEvidence(evidenceData.evidence || []);

        // ComplianceFindings don't have a dedicated "by inspection" filter -
        // fetch the project's findings and narrow to this inspection client-side.
        if (loadedInspection?.projectId) {
          const findingData = await inspectionService.getFindings({ projectId: loadedInspection.projectId });
          if (!cancelled) {
            setFindings((findingData.findings || []).filter((f) => f.inspectionId === inspectionId));
          }
        }
      } catch (error) {
        console.error('Failed to load inspection detail:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [inspectionId]);

  if (!inspectionId) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="inspection-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><i className="fas fa-clipboard-check"></i> Inspection Details</h2>
          <button className="close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>

        <div className="modal-content">
          {loading || !inspection ? (
            <div className="idm-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading inspection...</p>
            </div>
          ) : (
            <>
              <div className="idm-summary">
                <div className="idm-summary-item">
                  <span className="idm-label">Project</span>
                  <span className="idm-value">{inspection.project?.name || '—'}</span>
                </div>
                <div className="idm-summary-item">
                  <span className="idm-label">Category</span>
                  <span className="idm-value">{formatCategory(inspection.category)}</span>
                </div>
                <div className="idm-summary-item">
                  <span className="idm-label">Location</span>
                  <span className="idm-value">{inspection.location || '—'}</span>
                </div>
                <div className="idm-summary-item">
                  <span className="idm-label">Submitted</span>
                  <span className="idm-value">{formatDateTime(inspection.submittedAt)}</span>
                </div>
                <div className="idm-summary-item">
                  <span className="idm-label">Inspector</span>
                  <span className="idm-value">
                    {inspection.inspector ? `${inspection.inspector.firstName} ${inspection.inspector.lastName}` : '—'}
                  </span>
                </div>
                <div className="idm-summary-item">
                  <span className="idm-label">Completion</span>
                  <span className="idm-value">{inspection.completionPercentage}%</span>
                </div>
                <div className="idm-summary-item">
                  <span className="idm-label">Overall Compliance</span>
                  <span className={`compliance-score ${inspection.overallCompliance === 'compliant' ? 'high' : inspection.overallCompliance === 'partial' ? 'medium' : 'low'}`}>
                    {OVERALL_LABEL[inspection.overallCompliance] || inspection.overallCompliance}
                  </span>
                </div>
                {inspection.gpsLocation && (
                  <div className="idm-summary-item">
                    <span className="idm-label">GPS</span>
                    <span className="idm-value">
                      {inspection.gpsLocation.latitude?.toFixed(6)}, {inspection.gpsLocation.longitude?.toFixed(6)}
                      {inspection.gpsLocation.accuracy ? ` (±${Math.round(inspection.gpsLocation.accuracy)}m)` : ''}
                    </span>
                  </div>
                )}
              </div>

              <h3 className="idm-section-title"><i className="fas fa-list-check"></i> Checklist</h3>
              <div className="idm-checklist">
                {(inspection.checklist || []).map((item, idx) => (
                  <div key={idx} className={`idm-checklist-item idm-status-${item.status}`}>
                    <div className="idm-checklist-item-header">
                      <span className="idm-checklist-item-text">{item.text}</span>
                      <span className={`status-badge ${item.status === 'compliant' ? 'status-compliant' : item.status === 'non_compliant' ? 'status-non_compliant' : 'status-not_applicable'}`}>
                        {ITEM_STATUS_LABEL[item.status] || item.status}
                      </span>
                    </div>
                    {item.notes && <p className="idm-checklist-item-notes">{item.notes}</p>}
                  </div>
                ))}
                {(!inspection.checklist || inspection.checklist.length === 0) && (
                  <p className="idm-empty">No checklist items recorded.</p>
                )}
              </div>

              {(inspection.overallFindings || inspection.recommendations) && (
                <>
                  <h3 className="idm-section-title"><i className="fas fa-file-alt"></i> Findings & Recommendations</h3>
                  {inspection.overallFindings && <p className="idm-text-block">{inspection.overallFindings}</p>}
                  {inspection.recommendations && (
                    <>
                      <h4 className="idm-subsection-title">Recommendations</h4>
                      <p className="idm-text-block">{inspection.recommendations}</p>
                    </>
                  )}
                </>
              )}

              <h3 className="idm-section-title"><i className="fas fa-exclamation-circle"></i> Compliance Findings Raised ({findings.length})</h3>
              {findings.length === 0 ? (
                <p className="idm-empty">No compliance findings were raised from this inspection.</p>
              ) : (
                <div className="idm-findings-list">
                  {findings.map((f) => (
                    <div key={f.id} className="idm-finding-item">
                      <div className="idm-checklist-item-header">
                        <span className="idm-checklist-item-text">{f.title}</span>
                        <span className={`rag-badge rag-${f.rag.toLowerCase()}`}>{f.rag}</span>
                      </div>
                      <p className="idm-checklist-item-notes">
                        Severity: {f.severity} &middot; Status: {FINDING_STATUS_LABEL[f.status] || f.status}
                        {f.correctiveAction ? ` \u2014 ${f.correctiveAction}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <h3 className="idm-section-title"><i className="fas fa-camera"></i> Evidence ({evidence.length})</h3>
              {evidence.length === 0 ? (
                <p className="idm-empty">No photos attached to this inspection.</p>
              ) : (
                <div className="idm-evidence-grid">
                  {evidence.map((item) => (
                    <a key={item.id} href={item.fileUrl} target="_blank" rel="noopener noreferrer" className="idm-evidence-tile">
                      <img src={item.fileUrl} alt="Evidence" />
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default InspectionDetailModal;
